import React, { useEffect, useRef, useState, useContext, useImperativeHandle, forwardRef } from 'react';
import { Box } from '@chakra-ui/react';
import ReactFlow, { useNodesState, useEdgesState, ConnectionLineType, ReactFlowInstance } from 'reactflow';
import 'reactflow/dist/style.css';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { ProjectContext } from "./InvestmentScreeningDetail";
import { UniqueProcessType, } from '../../contexts/types';
import './ReportAndDraft.css';
import ProcessNode from './ProcessNode';
import GroupNode from './GroupNode';
import { fetchProcess, fetchPartNoListForDisplay, buildHierarchyProcess, buildOptimalPosition, buildFlowData } from './ProcessFlow';

// -----------------------------
// 定数
// -----------------------------
/** カスタムノードタイプ */
const nodeTypes = {
  custom: ProcessNode,
  group: GroupNode,
};

// -----------------------------
// インタフェース
// -----------------------------
interface ProcessFlowComponentProps {
  projectId: string;
  zoomControl?: boolean;
}

interface ProcessFlowComponentHandle {
  fitView: () => void;
}

/** 申請書画面 プロセスフローデータ作成 */
const ProcessFlowComponent = forwardRef<ProcessFlowComponentHandle, ProcessFlowComponentProps>(({ projectId }, ref) => {
  const WebAPI = useContext(WebAPIContext);
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? null;
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);

  /* fitView外部呼出し **/
  useImperativeHandle(ref, () => ({
    fitView: () => {
      if (rfInstance) {
        rfInstance.fitView();
      }
    }
  }));

  // -----------------------------
  // useState
  // -----------------------------
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [uniqueProcessData, setUniqueProcessData] = useState<UniqueProcessType[]>([]);

  // 初期処理
  useEffect(() => {
    drawProcessFlow();
  }, [projectId]);

  /**
   * プロセスフロー描画
   */
  const drawProcessFlow = async () => {
    const uniqueProcessData = await fetchProcess(projectId, null, WebAPI, null, reviewDate) ?? [];
    if ((uniqueProcessData?.length ?? 0) === 0) {
      return;
    }
    const partNoList = await fetchPartNoListForDisplay(projectId, WebAPI, reviewDate);
    const rootProcess = buildHierarchyProcess(uniqueProcessData);
    const maxLevelProcess = uniqueProcessData.reduce((max, current) => {
      return (current.uniqueLevel ?? Number.MAX_VALUE) > (max.uniqueLevel ?? Number.MAX_VALUE) ? current : max;
    });
    buildOptimalPosition(rootProcess, 0, maxLevelProcess.uniqueLevel ?? 0);
    setUniqueProcessData(uniqueProcessData);
    const { nodes, edges } = buildFlowData(rootProcess, undefined, undefined, partNoList);

    // ノード設定
    setNodes(nodes);
    // エッジ設定
    setEdges(edges);
  }

  return (
    <Box style={{ height: 750, overflow: 'auto', border: '1px solid #ccc' }} ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodesDraggable={false}
        nodeTypes={nodeTypes}
        connectionLineType={ConnectionLineType.SmoothStep}
        nodesConnectable={false}
        selectionOnDrag={false}
        fitView
        minZoom={0.01}
        onInit={setRfInstance}
      />
    </Box>
  );
});

export default ProcessFlowComponent;
